'use client';

import { useCustomSearchParams } from '@/hooks/useCustomSearchParams';
import { getScheduleDay, IScheduleDay } from '@/tools/locations';
import { getStateAbbreviation } from '@lamesarv-sdk/tools';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ILocationItemProps extends IScheduleDay {
  classes_schedule?: string;
  classes_schedule_status?: string;
  classes_schedule_time?: string;
  customNow?: dayjs.Dayjs;
}

const LocationItemStatus = ({
  classes_schedule,
  classes_schedule_status,
  classes_schedule_time,
  defaultClosed,
  end,
  endLocal,
  start,
  startLocal,
  customNow,
}: ILocationItemProps) => {
  const StatusClosed = () => <div className={twMerge('text-[14px] text-red-500', classes_schedule_status)}>Closed</div>;
  const StatusOpen = () => <div className={twMerge('text-[14px] text-green-500', classes_schedule_status)}>Open</div>;

  if (defaultClosed) {
    return <StatusClosed />;
  }

  const now = customNow || dayjs().tz(dayjs.tz.guess());

  const isOpen = now.isBetween(startLocal, endLocal, null, '[)');

  return (
    <div className={twMerge('flex flex-row gap-1', classes_schedule)}>
      {isOpen ? <StatusOpen /> : <StatusClosed />}
      <div className={twMerge('text-[14px] text-black-350', classes_schedule_time)}>&#x25CF;</div>
      <div className={twMerge('text-[14px] text-black-350', classes_schedule_time)}>
        {isOpen && `Closes at ${end?.format('h:mm a')}`}
        {!isOpen && `Opens at ${start?.format('h:mm a')}`}
      </div>
    </div>
  );
};

export interface ILocationItem {
  address: string;
  city: string;
  schedule_saturday?: string;
  schedule_sunday?: string;
  schedule_weekdays?: string;
  state: string;
  title: string;
  zipcode: string;
  geo_location?: { lat: number; lng: number };
  classes?: string;
  classes_address?: string;
  classes_location?: string;
  classes_schedule?: string;
  classes_schedule_status?: string;
  classes_schedule_time?: string;
  classes_title?: string;
  url?: string;
  target_blank?: boolean;
}

export const LocationItem = ({
  address,
  city,
  schedule_saturday,
  schedule_sunday,
  schedule_weekdays,
  state,
  title,
  zipcode,
  classes,
  classes_address,
  classes_location,
  classes_schedule,
  classes_schedule_status,
  classes_schedule_time,
  classes_title,
  url,
  target_blank,
}: ILocationItem) => {
  const { searchParams } = useCustomSearchParams();

  const state_code = getStateAbbreviation(state) || state;

  let customNow: dayjs.Dayjs | undefined = undefined;

  const debugAddTime = searchParams.get('add-time');
  const debugSetTime = searchParams.get('set-time');

  if (debugAddTime) {
    const hours = parseInt(debugAddTime);
    if (!isNaN(hours)) {
      customNow = dayjs().tz(dayjs.tz.guess()).add(hours, 'hour');
    }
  }

  if (debugSetTime) {
    const parsed = dayjs(debugSetTime);
    if (parsed.isValid()) {
      customNow = parsed.tz(dayjs.tz.guess(), true);
    }
  }

  const timeSchedule = getScheduleDay({
    state,
    city,
    schedule_sunday: schedule_sunday || '',
    schedule_weekdays: schedule_weekdays || '',
    schedule_saturday: schedule_saturday || '',
    asTimeZone: dayjs.tz.guess(),
    customNow,
  });

  return (
    <Link
      href={url}
      target={target_blank ? '_blank' : '_self'}
      className={twMerge('flex flex-col gap-3 py-6 px-6 border-t border-black-200', classes)}
    >
      <div className={twMerge('text-[14px] text-black-500 font-semibold', classes_title)}>{title}</div>
      <div className="flex flex-col gap-2">
        <div className={twMerge('text-[14px] text-black-350 leading-none', classes_address)}>{address}</div>
        <div className={twMerge('text-[14px] text-black-350 leading-none', classes_location)}>
          {city}, {state_code.toUpperCase()}, {zipcode}
        </div>
      </div>
      <LocationItemStatus
        {...timeSchedule}
        classes_schedule={twMerge('flex flex-row gap-1', classes_schedule)}
        classes_schedule_status={classes_schedule_status}
        classes_schedule_time={classes_schedule_time}
        customNow={customNow}
      />
    </Link>
  );
};
